//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { IPromise, IQService } from 'angular';
import { FeatureNames } from '@bvengineering/curalate-features';
import moment from 'moment-timezone';
import _ from 'underscore';

// Pulling in these classes as types below isn't sufficient to get them onto webpack's radar,
// so make sure the entirety of the file is also imported.
import '@services/metrics-gateway-api-service';

import * as instagramAudienceMetricsApi from '@apis/gateway-instagram/instagramAudienceMetricsApi';
import * as instagramUserApi from '@apis/instagram-user/instagramUserApi';
import { IDefaultGraphApiUserInsightsRequest } from '@apis/instagram-user/instagramUserApiModel';
import { localDateToPst } from '@instagram-account/date-range-helper';
import { INSTAGRAM_USER_STATS_ACTIONS } from '@instagram-account/instagram-account-constants/instagram-user-stats-constants';
import SummaryStatsIntervalHelper from '@instagram-account/instagram-account-constants/summary-stats-interval-helper';
import instagramAccountModule from '@instagram-account/instagram-account-module';
import {
  TRAFFIC_BY_CHANNELS_VALUES,
  UMS_CHANNELS,
  UMS_FIELDS,
} from '@instagram-account/instagram-account-summary-channels/instagram-account-summary-channels-values';
import { SUMMARY_TREND_VALUES } from '@instagram-account/instagram-account-summary-trends/instagram-account-summary-trends-values';
import { fillInMissingDates } from '@instagram-account/redux/engagement/instagramAccountEngagementUtils';
import { ICuralateStore } from '@models/AppState';
import DateString from '@models/DateString';
import { GraphApiUserInsightsPeriod } from '@models/GraphApiUserInsightsPeriod';
import {
  PostsStatAggregationArray,
  PostsStatHistogramArray,
} from '@models/IAggregationResult';
import InstagramUserId from '@models/InstagramUserId';
import Interval from '@models/Interval';
import { IGraphStatsResponse } from '@models/MetricsGatewayApiModels';
import { IUnisonDateRange } from '@models/UnisonTypes';
import TMetricsGatewayApiService from '@services/metrics-gateway-api-service';
import {
  getDateRangeDefault,
  getMinDateRangeDate,
} from '@source-page/source-page-constants/date-range-constants';
import { crl8 } from 'app/curalate.globals';
import { ReduxDispatch } from 'app/curalate.store';

function requestSummaryInstagramUserStats() {
  return {
    type: INSTAGRAM_USER_STATS_ACTIONS.REQUEST_SUMMARY_INSTAGRAM_USER_STATS,
  };
}

function receiveSummaryInstagramUserStats(
  trendStats: IUserTrendStat[],
  aggregateStats: IUserAggregateStats,
  aggregateComparisonStats: IUserAggregateStats,
) {
  return {
    payload: {
      trendStats,
      aggregateStats,
      aggregateComparisonStats,
    },
    type: INSTAGRAM_USER_STATS_ACTIONS.RECEIVE_SUMMARY_INSTAGRAM_USER_STATS,
  };
}

function requestSummaryTrafficStats() {
  return {
    type: INSTAGRAM_USER_STATS_ACTIONS.REQUEST_SUMMARY_TRAFFIC_STATS,
  };
}

function receiveSummaryTrafficStats(referralStats, trafficByChannelStats) {
  return {
    payload: {
      referralStats,
      trafficByChannelStats,
    },
    type: INSTAGRAM_USER_STATS_ACTIONS.RECEIVE_SUMMARY_TRAFFIC_STATS,
  };
}

function requestHMSAudienceInsightsStart() {
  return {
    type: INSTAGRAM_USER_STATS_ACTIONS.REQUEST_HMS_AUDIENCE_INSIGHTS_START,
  };
}

function receiveHMSAudienceInsightsStart(payload: {
  date: Date;
  dateRange: IUnisonDateRange;
}) {
  return {
    type: INSTAGRAM_USER_STATS_ACTIONS.RECEIVE_HMS_AUDIENCE_INSIGHTS_START,
    payload,
  };
}

function flattenInsightsValues(values, fieldName) {
  return _(values).map((insightValue: any) => {
    return {
      [fieldName]: insightValue.value,
      date: insightValue.endTime,
    };
  });
}

function normalize(
  data: Array<Partial<IUserTrendStat>>,
): Array<Partial<IUserTrendStat>> {
  return _(data).map((datum: any) => {
    return _.extend({}, datum, {
      // Daily aggregate stats from facebook are returned with an endTime of 8:00GMT
      // To make sure we get the same Date out of timestamps across timezones, force the date into the PST
      // timezone
      readableDate: moment(datum.date)
        .tz('America/Los_Angeles')
        .startOf('day')
        .format('YYYY-MM-DD'),
    });
  });
}

function combineStats(
  onsiteDailyInsights: ICorrectedDailyInsights,
  engagementRateData: Array<Partial<IUserTrendStat>>,
): IUserTrendStat[] {
  const safeDailyRevenue = onsiteDailyInsights?.revenue || [];
  const dailyRevenue = flattenInsightsValues(
    safeDailyRevenue,
    SUMMARY_TREND_VALUES.REVENUE,
  );
  const safeDailyTransactions = onsiteDailyInsights?.transactions || [];
  const dailyTransactions = flattenInsightsValues(
    safeDailyTransactions,
    SUMMARY_TREND_VALUES.TRANSACTIONS,
  );
  const safeDailyTraffic = onsiteDailyInsights?.ecomSessions || [];
  const dailyTraffic = flattenInsightsValues(
    safeDailyTraffic,
    SUMMARY_TREND_VALUES.TRAFFIC,
  );

  const normalizedData = normalize([
    ...engagementRateData,
    ...dailyRevenue,
    ...dailyTransactions,
    ...dailyTraffic,
  ]);
  return _(normalizedData)
    .chain()
    .reduce((memo, val) => {
      const index = moment(val.readableDate).toString();
      memo[index] = _.extend({}, memo[index], val);
      return memo;
    }, {})
    .values()
    .value();
}

function getQueryParamsForUms(interval: Interval) {
  return {
    interval: correctIntervalForUMS(interval),
    ecommerceSessionType: 'Product',
    requestContext: 'Instagram',
  };
}

// Note: The function below is used to manually change the interval before passing it
// off to UMS. This is due to inconsistencies in the way UMS and instagram endpoints
// above handle intervals. UMS intervals are shifted back by a few hours, and include
// the end date. The metrics returned from the instagram calls do not include the end date.
//
// For example, if you request data between 9/5 and 9/12 across both 'kinds' of endpoints
// * The results from instagram will contain data timestamped between 9/5 and 9/11 @ 7am UTC
// * The results from ums will contain data timestamped between 9/5 and 9/12 @ 12am UTC
//
// As such, we manually change the intervals below so that the results are consistent
// with the results from the functions above.
function correctIntervalForUMS(interval: Interval): Interval {
  return SummaryStatsIntervalHelper.shortenInterval(interval, 1, false);
}

// Timestamps from UMS are built in terms of midnight GMT. Instagram
// insights are 7am GMT. In order to properly zip the values together,
// we shift the UMS timestamps ahead by 7 hours during DST (and 8 hours
// during daylight time). Otherwise, differences in timezones will break
// the zipping process.
function correctUMSTimestampForGraph(umsTimestamp) {
  const DST_OFFSET = 7;
  const DAYLIGHT_TIME_OFFSET = 8;
  const isDST = moment(umsTimestamp).isDST();

  return (
    moment
      .utc(umsTimestamp)
      .add(isDST ? DST_OFFSET : DAYLIGHT_TIME_OFFSET, 'hours')
      .unix() * 1000
  );
}

function correctTouchpointStatsForGraph(
  channelStats,
  shopTagsEnabled: boolean,
) {
  const sumStatValues = (stats) => _.chain(stats).values().sum().value();

  const getStat = (channel, field, fallback?) => {
    const stats = _(channelStats).findWhere({ touchpoint: channel });
    return stats?.[field] ?? fallback;
  };

  const getReferralStats = (field) => {
    if (shopTagsEnabled) {
      return {
        LIKE2BUY: getStat(UMS_CHANNELS.LIKE2BUY, field, 0),
        STORIES: getStat(UMS_CHANNELS.STORIES, field, 0),
        PROMOTED_POSTS: getStat(UMS_CHANNELS.PROMOTED_POSTS, field, 0),
        SHOP_TAGS: getStat(UMS_CHANNELS.SHOP_TAGS, field, 0),
      };
    } else {
      return {
        LIKE2BUY: getStat(UMS_CHANNELS.LIKE2BUY, field, 0),
        STORIES: getStat(UMS_CHANNELS.STORIES, field, 0),
        PROMOTED_POSTS: getStat(UMS_CHANNELS.PROMOTED_POSTS, field, 0),
      };
    }
  };

  const getChannelStats = (channel) => {
    const visits = getStat(channel, UMS_FIELDS.VISITS, 0);
    const transactions = getStat(channel, UMS_FIELDS.TRANSACTIONS, 0);
    return {
      metrics: {
        visits,
        transactions,
        revenue: getStat(channel, UMS_FIELDS.REVENUE, 0),
        conversionRate: visits !== 0 ? transactions / visits : 0,
      },
    };
  };

  const trafficReferralStats = getReferralStats(UMS_FIELDS.VISITS);
  const revenueReferralStats = getReferralStats(UMS_FIELDS.REVENUE);

  const referralStats = {
    traffic: {
      ...trafficReferralStats,
      totalCount: sumStatValues(trafficReferralStats),
    },
    revenue: {
      ...revenueReferralStats,
      totalCount: sumStatValues(revenueReferralStats),
    },
  };

  const like2BuyStats = getChannelStats(UMS_CHANNELS.LIKE2BUY);
  const promotedPostsStats = getChannelStats(UMS_CHANNELS.PROMOTED_POSTS);
  const storiesStats = getChannelStats(UMS_CHANNELS.STORIES);

  const trafficByChannelStats = [
    { name: TRAFFIC_BY_CHANNELS_VALUES.LIKE2BUY, ...like2BuyStats },
    { name: TRAFFIC_BY_CHANNELS_VALUES.PROMOTED_POSTS, ...promotedPostsStats },
    { name: TRAFFIC_BY_CHANNELS_VALUES.STORIES, ...storiesStats },
  ];

  if (shopTagsEnabled) {
    const shoppingStats = getChannelStats(UMS_CHANNELS.SHOP_TAGS);
    trafficByChannelStats.push({
      name: TRAFFIC_BY_CHANNELS_VALUES.SHOP_TAGS,
      ...shoppingStats,
    });
  }

  return {
    referralStats,
    trafficByChannelStats,
  };
}

function correctOnsiteDailyInsightsForGraph(
  onsiteDailyInsights: IGraphStatsResponse[],
): ICorrectedDailyInsights {
  const dailyRevenue = _(onsiteDailyInsights).map<ICorrectedDailyInsight>(
    (insight: any) => ({
      endTime: correctUMSTimestampForGraph(insight.timestamp),
      value: insight.revenue,
    }),
  );
  const dailyTransactions = _(onsiteDailyInsights).map<ICorrectedDailyInsight>(
    (insight: any) => ({
      endTime: correctUMSTimestampForGraph(insight.timestamp),
      value: insight.transactions,
    }),
  );
  const dailySessions = _(onsiteDailyInsights).map<ICorrectedDailyInsight>(
    (insight: any) => ({
      endTime: correctUMSTimestampForGraph(insight.timestamp),
      value: insight.ecomSessions,
    }),
  );

  return {
    revenue: dailyRevenue,
    transactions: dailyTransactions,
    ecomSessions: dailySessions,
  };
}

function buildEngagementRateStats(
  postStatsData: PostsStatHistogramArray,
  startDate: Date,
  endDate: Date,
): Array<Partial<IUserTrendStat>> {
  const sparseEngagementRates = _(postStatsData)
    .chain()
    .sortBy('date')
    .map((day) => {
      return {
        engagementRate: getEngagementRate(day.stats),
        date: day.date,
      };
    })
    .value();

  return fillInMissingDates(
    sparseEngagementRates,
    startDate,
    endDate,
    (date) => ({
      engagementRate: 0,
      date,
    }),
  );
}

function getEngagementRate(postStats: PostsStatAggregationArray) {
  const comments = _.findWhere(postStats, { name: 'comments' })?.value.sum ?? 0;
  const likes = _.findWhere(postStats, { name: 'likes' })?.value.sum ?? 0;
  const reach = _.findWhere(postStats, { name: 'reach' })?.value.sum ?? 0;
  return reach ? (comments + likes) / reach : 0;
}

function sumField(data, fieldName) {
  return (
    data.map((d) => d[fieldName] || 0).reduce((tot, num) => tot + num, 0) || 0
  );
}

function calculateAggregateUserStats(
  stats: IUserTrendStat[],
  postAggregateStats: PostsStatAggregationArray,
): IUserAggregateStats {
  const { REVENUE, TRANSACTIONS, TRAFFIC } = SUMMARY_TREND_VALUES;

  const totalRevenue = sumField(stats, REVENUE);
  const totalVisits = sumField(stats, TRAFFIC);
  const totalTransactions = sumField(stats, TRANSACTIONS);

  const totalEngagementRate = getEngagementRate(postAggregateStats);

  return {
    totalEngagementRate,
    totalRevenue,
    totalVisits,
    totalTransactions,
  };
}

export type IInstagramUserStatsActions = ReturnType<
  typeof InstagramUserStatsActions
>;

export interface IUserAggregateStats {
  totalEngagementRate: number;
  totalRevenue: number;
  totalVisits: number;
  totalTransactions: number;
}

export interface IUserTrendStat {
  date: Date;
  engagementRate: number;
  readableDate: DateString;
  revenue: number;
  traffic: number;
  transactions: number;
}

interface ICorrectedDailyInsights {
  revenue: ICorrectedDailyInsight[];
  transactions: ICorrectedDailyInsight[];
  ecomSessions: ICorrectedDailyInsight[];
}

interface ICorrectedDailyInsight {
  endTime: number;
  value: number;
}

function InstagramUserStatsActions(
  $q: IQService,
  MetricsGatewayApiService: TMetricsGatewayApiService,
) {
  function fetchUserStats(
    instagramUserId: InstagramUserId,
    params: IDefaultGraphApiUserInsightsRequest,
  ): IPromise<{
    trendStats: IUserTrendStat[];
    aggregateStats: IUserAggregateStats;
    aggregateComparisonStats: IUserAggregateStats;
  }> {
    const onsiteDailyInsightsPromise = MetricsGatewayApiService.getGraphStats(
      getQueryParamsForUms(params.interval),
    );

    // These params are set up to exactly match what we are sending on the Engagement page to retrieve and display
    // Total Engagement Rate trend and summary stats. See instagram-account-posts-actions.ts for reference.
    const startDate = moment(params.interval.split('/')[0]).toDate();
    const endDate = moment(params.interval.split('/')[1])
      .subtract(1, 'day')
      .endOf('day')
      .toDate();
    const postStatsParams = {
      startTime: localDateToPst(startDate).getTime(),
      endTime: localDateToPst(endDate).getTime(),
    };

    const startTime = startDate.getTime();
    const duration = endDate.getTime() - startTime;
    const comparisonStartDate = moment(startTime - duration).toDate();

    let onsiteDailyComparisonInsightsPromise: IPromise<IGraphStatsResponse[]> =
      Promise.resolve([]);
    const comparisonInterval = Interval.fromDateRange({
      startDate: comparisonStartDate,
      endDate: startDate,
      alias: 'custom',
    });
    onsiteDailyComparisonInsightsPromise =
      MetricsGatewayApiService.getGraphStats(
        getQueryParamsForUms(comparisonInterval),
      );

    const postStatsPromise =
      instagramUserApi.getDefaultInstagramUserPostsStats(postStatsParams);

    return $q
      .all({
        onsiteDailyInsightsResp: onsiteDailyInsightsPromise,
        onsiteDailyComparisonInsightsResp: onsiteDailyComparisonInsightsPromise,
        postStatsResp: postStatsPromise,
        postStatsStartDate: startDate,
        postStatsEndDate: endDate,
      })
      .then(
        ({
          onsiteDailyInsightsResp,
          onsiteDailyComparisonInsightsResp,
          postStatsResp,
          postStatsStartDate,
          postStatsEndDate,
        }) => {
          const engagementRateData = buildEngagementRateStats(
            postStatsResp.histogram,
            postStatsStartDate,
            postStatsEndDate,
          );

          const postSummaryStats = postStatsResp.stats;

          const onsiteDailyInsights = correctOnsiteDailyInsightsForGraph(
            onsiteDailyInsightsResp,
          );

          const combinedStats = combineStats(
            onsiteDailyInsights,
            engagementRateData,
          );
          const aggregateStats = calculateAggregateUserStats(
            combinedStats,
            postSummaryStats,
          );

          const engagementRateComparisonData = buildEngagementRateStats(
            postStatsResp.comparisonHistogram,
            comparisonStartDate,
            postStatsStartDate,
          );
          const postSummaryComparisonStats = postStatsResp.comparisonStats;
          const onsiteDailyComparisonInsights =
            correctOnsiteDailyInsightsForGraph(
              onsiteDailyComparisonInsightsResp,
            );
          const combinedComparisonStats = combineStats(
            onsiteDailyComparisonInsights,
            engagementRateComparisonData,
          );
          const aggregateComparisonStats = calculateAggregateUserStats(
            combinedComparisonStats,
            postSummaryComparisonStats,
          );

          // Since we are gating the display of the comparison stats,
          // assigning aggregateStats to the aggregateComparisonStats field for type correction.
          // This can be updated once we GA the comparison stats feature
          return {
            trendStats: combinedStats,
            aggregateStats,
            aggregateComparisonStats,
          };
        },
      );
  }

  function fetchSummaryTrafficStats() {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const state = getState();

      const shopTagsEnabled =
        state.session.features.instagram_shop_tags_insights;

      const defaultDateRange = state.instagramUserStats.summaryDefaultDateRange;
      const interval = defaultDateRange
        ? SummaryStatsIntervalHelper.fromDateRange(defaultDateRange)
        : SummaryStatsIntervalHelper.pastDays(6);

      dispatch(requestSummaryTrafficStats());

      const touchpointStatsPromise =
        MetricsGatewayApiService.getTouchpointStats(
          getQueryParamsForUms(interval),
        );

      return touchpointStatsPromise.then((touchpointStatsResp) => {
        const { referralStats, trafficByChannelStats } =
          correctTouchpointStatsForGraph(touchpointStatsResp, shopTagsEnabled);

        dispatch(
          receiveSummaryTrafficStats(referralStats, trafficByChannelStats),
        );
      });
    };
  }

  function fetchSummaryUserStats(instagramUserId: InstagramUserId) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      dispatch(requestSummaryInstagramUserStats());
      const state = getState();

      const defaultDateRange = state.instagramUserStats.summaryDefaultDateRange;
      const interval = defaultDateRange
        ? SummaryStatsIntervalHelper.fromDateRange(defaultDateRange)
        : SummaryStatsIntervalHelper.pastDays(6);
      const period = [
        GraphApiUserInsightsPeriod.Day,
        GraphApiUserInsightsPeriod.Week,
      ];
      const params: IDefaultGraphApiUserInsightsRequest = { interval, period };

      return fetchUserStats(instagramUserId, params).then(
        ({ trendStats, aggregateStats, aggregateComparisonStats }) => {
          dispatch(
            receiveSummaryInstagramUserStats(
              trendStats,
              aggregateStats,
              aggregateComparisonStats,
            ),
          );
        },
      );
    };
  }

  function fetchHMSAudienceInsightsStartDate() {
    return (dispatch: ReduxDispatch) => {
      dispatch(requestHMSAudienceInsightsStart());

      return instagramAudienceMetricsApi
        .getEarliestAudienceInsightDate()
        .then((result) => {
          const date = getMinDateRangeDate(result?.dateTime ?? new Date());
          const dateRange = getDateRangeDefault(date);

          dispatch(receiveHMSAudienceInsightsStart({ date, dateRange }));
        });
    };
  }

  return {
    fetchSummaryUserStats,
    fetchHMSAudienceInsightsStartDate,
    fetchSummaryTrafficStats,
  };
}

instagramAccountModule.service('InstagramUserStatsActions', [
  '$q',
  'MetricsGatewayApiService',
  InstagramUserStatsActions,
]);
